import { ShowInterface } from 'src/models/Show';
import {
  ShowResponse,
  ShowsResponse,
  ShowDashboardResponse,
  ShowsDashboardResponse,
  ShowsResponseSimplified,
  ShowResponseSimplified,
  ShowsAudiencePageResponse,
  GetShowResponse
} from '../models/types'
import { fetchWithAuth, defaultFetch } from '../utils/auth';

export const readShowDashboard = async (BACKEND_URL: string, showId: string): Promise<ShowDashboardResponse> => {
    try {
        const res = await fetchWithAuth(BACKEND_URL + `/api/backend/v1/shows/dashboard/${showId}`);

        if (!res.ok || (res.status >= 400 && res.status < 600)) {
            throw new Error(`Server not returned a valid response. Error: ${res.status}`);
        }

        const resJson = await res.json();

        return new ShowDashboardResponse({ data: resJson });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load show dashboard'
        }
        return new ShowDashboardResponse({ error: message })
    }
}

export const readShowsDashboard = async (BACKEND_URL: string): Promise<ShowsDashboardResponse> => {
    try {
        const res = await fetchWithAuth(BACKEND_URL + "/api/backend/v1/shows/dashboard");

        if (!res.ok || (res.status >= 400 && res.status < 600)) {
            throw new Error(`Server not returned a valid response. Error: ${res.status}`);
        }

        const resJson = await res.json();

        return new ShowsDashboardResponse({ data: resJson });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows dashboards'
        }
        return new ShowsDashboardResponse({ error: message })
    }
}

export const readShowsAndSessions = async (): Promise<ShowsResponseSimplified> => {
    try {
        const res = await fetchWithAuth(`/api/backend/v1/shows/shows-with-sessions`)
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        return new ShowsResponseSimplified({ data: resJson?.shows });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows and sessions'
        }
        return new ShowsResponseSimplified({ error: message })
    }
}

export const readUpcomingShow = async (): Promise<ShowResponseSimplified> => {
    try {
        const res = await defaultFetch(`/api/backend/v1/shows/upcoming-show`)
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        return new ShowResponseSimplified({ data: resJson?.show });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load upcoming shows'
        }
        return new ShowResponseSimplified({ error: message })
    }
}

export const readShowsAudience = async (): Promise<ShowsAudiencePageResponse> => {
    try {
        const res = await defaultFetch(`/api/backend/v1/shows/audience`)
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        return new ShowsAudiencePageResponse({ data: resJson?.shows });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows'
        }
        return new ShowsAudiencePageResponse({ error: message })
    }
};

export const readShows = async (withoutAuth? : boolean | undefined): Promise<ShowsResponse> => {
    try {
        const res = withoutAuth ? await defaultFetch(`/api/backend/v1/shows?excludeDeleted=true`) : await fetchWithAuth(`/api/backend/v1/shows?excludeDeleted=true`);
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        return new ShowsResponse({ data: resJson?.shows });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows'
        }
        return new ShowsResponse({ error: message })
    }
};

export const readShow = async (showIdentifier: number | string): Promise<ShowResponse> => {
    try {
        const res = await defaultFetch(`/api/backend/v1/shows/${showIdentifier}`)
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        return new ShowResponse({ data: resJson });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows'
        }
        return new ShowResponse({ error: message })
    }
};

export const readShowById = async (showId: number): Promise<GetShowResponse> => {
    try {
        const res = await fetchWithAuth(`/api/backend/v1/shows/${showId}`)
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        return new GetShowResponse({ data: resJson });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows'
        }
        return new GetShowResponse({ error: message })
    }
};

export const readTermsAndConditions = async (termsAndConditionsId: number): Promise<any> => {
    try {
        let url = `/api/backend/termsandconditions?id=${termsAndConditionsId}`
        const res = await defaultFetch(url);
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        return res
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to fetch Terms and Conditions'
        }
        return { error: message };
    }
};

export const createShow = async (show: ShowInterface): Promise<any> => {
    try {
        const image = show.image;
        const termsAndConditions = show.termsAndConditions;

        const fileKeys = ['name', 'type']
        const body = new FormData()
        for (const key of Object.keys(show) as Array<keyof ShowInterface>) {
            body.append(`Show.${key}`, show[key])
        }

        if (image != null) {
            body.append(`Image`, image)
            for (let imgKey of fileKeys) {
                body.append(`Image.${imgKey}`, image[imgKey])
            }
        }

        if (termsAndConditions != null) {
            body.append(`TermsAndConditions`, termsAndConditions)
            for (let fileKey of fileKeys) {
                body.append(`TermsAndConditions.${fileKey}`, termsAndConditions[fileKey])
            }
        }

        const res = await fetchWithAuth(`/api/backend/v1/shows`, {
            method: 'POST',
            body
        })
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text()
            console.warn(resText)
            throw new Error("Server not returned a valid response")
        }
        const showId = await res.text()
        return {
            showId,
        }
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows'
        }
        return { error: message }
    }
};

export const editShow = async (show: any): Promise<any> => {
    try {
        const image = show.image;
        const termsAndConditions = show.termsAndConditions;

        const fileKeys = ['name', 'type']
        const body = new FormData()
        for (let key in show) {
            body.append(`Show.${key}`, show[key])
        }
        if (image != null) {
            body.append(`Image`, image)
            for (let imgKey of fileKeys) {
                body.append(`Image.${imgKey}`, image[imgKey])
            }
        }
        if (termsAndConditions != null) {
            body.append(`TermsAndConditions`, termsAndConditions)
            for (let fileKey of fileKeys) {
                body.append(`TermsAndConditions.${fileKey}`, termsAndConditions[fileKey])
            }
        }
        const res = await fetchWithAuth(`/api/backend/v1/shows`, {
            method: 'PUT',
            body
        })
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text()
            console.warn(resText)
            throw new Error("Server not returned a valid response")
        }
        return res
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows'
        }
        return { error: message }
    }
};

export const deleteShow = async (showId: number): Promise<any> => {
    try {
        const res = await fetchWithAuth(`/api/backend/v1/shows/${showId}`, {
            method: 'DELETE',
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
    } catch (error) {
        let message = 'Unable to delete session';

        if (error instanceof Error) {
            message = error.message;
        }

        return { error: message };
    }
};